// Direct imports for optimal tree shaking
import { Scene } from "@babylonjs/core/scene";
import { WebXRExperienceHelper } from "@babylonjs/core/XR/webXRExperienceHelper";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes";
import { WebXRDomOverlay } from "@babylonjs/core/XR/features/WebXRDOMOverlay";
import { WebXRInput } from "@babylonjs/core/XR/webXRInput";
import { WebXRControllerPointerSelection } from "@babylonjs/core/XR/features/WebXRControllerPointerSelection";
// Side-effect import: registers WebXRImageTracking with the features manager.
// The module calls WebXRFeaturesManager.AddWebXRFeature() at load time — without
// this import, tree-shaking drops the registration and enableFeature() fails.
import "@babylonjs/core/XR/features/WebXRImageTracking";
import type { WebXRImageTracking } from "@babylonjs/core/XR/features/WebXRImageTracking";
import { WebXRFeatureName } from "@babylonjs/core/XR/webXRFeaturesManager";
import { WebXRAbstractFeature } from "@babylonjs/core/XR/features/WebXRAbstractFeature";

import { Color4 } from "@babylonjs/core/Maths/math.color";
import { Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";

import { Logger } from "../utils/Logger";
import { ImageAlignmentOverlay } from "./ImageAlignmentOverlay";
import type { AlignmentResult } from "./ImageAlignmentOverlay";
import type { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import { projectQuaternionToYaw } from "./xrDragMath";
import { CONTROLS_MESH_SUFFIX } from "../behaviors/video";
import type { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import type { IWebXRTrackedImage } from "@babylonjs/core/XR/features/WebXRImageTracking";
import type { SixDofDragBehavior } from "@babylonjs/core/Behaviors/Meshes/sixDofDragBehavior";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { poseAutoSnapped, sceneFromObservation, type RigidPose } from "./localizerPose";
import { LocalizerStabilizer, nowSeconds } from "./localizerStabilizer";
import { legacyImageLocalizer, requireLocalizerSession, webxrImagePoseToLocalizer, type LocalizerSession } from "./localizerTracking";
import type { ScenePlacement } from "../cms/localizers";
import { poseAnchorHostForSession } from "./placementHost";

type SixDofDragCtor = typeof SixDofDragBehavior;

let _yawOnlySixDofCtor: SixDofDragCtor | null = null;

/**
 * Build (and cache) a SixDofDragBehavior subclass whose `_targetDrag`
 * yaw-projects the rotation delta. The first call mints the subclass from
 * `Base` and caches it; subsequent calls return the cached class and
 * **ignore** the `Base` argument. That's safe today because the dynamic
 * import of `sixDofDragBehavior` always returns the same module-level
 * class, but if Babylon ever ships a different SixDof variant alongside
 * this one, the cache would need to key on `Base`.
 */
function getYawOnlySixDofCtor(Base: SixDofDragCtor): SixDofDragCtor {
    if (_yawOnlySixDofCtor) return _yawOnlySixDofCtor;
    const scratch = new Quaternion();
    class YawOnlySixDof extends Base {
        protected override _targetDrag(
            worldDeltaPosition: Vector3,
            worldDeltaRotation: Quaternion,
        ): void {
            projectQuaternionToYaw(worldDeltaRotation, scratch);
            super._targetDrag(worldDeltaPosition, scratch);
        }
    }
    _yawOnlySixDofCtor = YawOnlySixDof;
    return YawOnlySixDof;
}

/**
 * XR session state for consumers.
 */
export enum XRSessionState {
    NOT_SUPPORTED = "not-supported",
    AVAILABLE = "available",
    ENTERING = "entering",
    ACTIVE = "active",
    EXITING = "exiting",
}

/**
 * Manages immersive-ar WebXR sessions for the trace-viewer.
 * Follows the feature module pattern (like ClickToSelect/ModelInfo).
 *
 * View-only: device tracking controls the camera, no teleportation or controllers.
 * Screen taps are forwarded as pointer events for interaction with the scene.
 */
export class XRManager {
    private readonly _scene: Scene;
    private readonly _traceScene: TraceScene;
    private _parentElement: HTMLElement;

    private _xrHelper: WebXRExperienceHelper | null = null;
    private _xrInput: WebXRInput | null = null;
    private _state: XRSessionState = XRSessionState.NOT_SUPPORTED;
    private _supported: boolean = false;

    private _savedClearColor: Color4 | null = null;
    private _uiEnabled: boolean = false;

    private _overlayContainer: HTMLDivElement | null = null;
    private _arButton: HTMLButtonElement | null = null;

    private _imageAlignmentConfig: { imageUrl: string; imageWidthMeters: number } | null = null;
    private _imageAlignment: ImageAlignmentOverlay | null = null;
    private _imageTrackingFeature: WebXRImageTracking | null = null;
    private _imageTrackingObserver: ReturnType<typeof WebXRImageTracking.prototype.onTrackedImageUpdatedObservable.add> | null = null;

    private _selection: LocalizerSession | null = null;
    private _sessionLocalizers: ScenePlacement[] = [];
    private _legacyTracking = false;
    private _stabilizer: LocalizerStabilizer | null = null;
    private _generation = 0;
    private _resetSpace: XRReferenceSpace | null = null;
    private _lastScenePose: RigidPose | null = null;
    private _restoreAnchorHost: (() => void) | null = null;
    private _restoreContentVisibility: (() => void) | null = null;
    private readonly _onTrackingReset = (): void => {
        this._generation++;
        this._unsubscribeTracking();
        this._disposeImageAlignment();
        this._stabilizer?.reset();
        this._lastScenePose = null;
        if (this._xrHelper) this._xrHelper.sessionManager.referenceSpace = this._xrHelper.sessionManager.baseReferenceSpace;
        this._setSceneContentVisible(false);
        const selected = this._sessionLocalizers[0];
        if (selected?.localizer) this._startImageAlignment({
            imageUrl: selected.localizer.imageUrl!, imageWidthMeters: selected.localizer.widthM!,
        }, !!this._imageTrackingFeature);
        this._subscribeTracking();
    };

    // XR drag interaction — allows any selected object to be grabbed/moved in XR
    private _xrDragSelectionHandler: ((e: Event) => void) | null = null;
    private _xrDragSixDof: SixDofDragBehavior | null = null;
    private _xrDragTarget: SceneObject | null = null;
    private _xrDragStartObserver: ReturnType<SixDofDragBehavior["onDragStartObservable"]["add"]> | null = null;
    /**
     * Optional hook into ClickToSelect. When the 6DoF drag starts, we notify
     * it so the grab release isn't interpreted as a click — XR controllers
     * synthesize near-zero screen-space deltas, defeating the usual threshold.
     */
    private _clickToSelect: { notifyDragOccurred(): void } | null = null;
    // Monotonically increasing per selection change. Persists for the
    // lifetime of the XRManager (NOT scoped to a single XR session) — it
    // only needs to be unique across in-flight `_onXRSelectionChanged`
    // awaits, which makes session-scoping unnecessary.
    private _xrDragSelectionEpoch = 0;
    private _disposed = false;

    constructor(scene: Scene, traceScene: TraceScene, parentElement: HTMLElement) {
        this._scene = scene;
        this._traceScene = traceScene;
        this._parentElement = parentElement;

        // Check support asynchronously (non-blocking)
        void this.checkSupport();
    }

    /**
     * Wire a ClickToSelect-like object so XR 6DoF grabs can suppress the
     * click-on-release fallback. Called by TraceViewer once both managers
     * are constructed.
     */
    setClickToSelect(cts: { notifyDragOccurred(): void } | null): void {
        this._clickToSelect = cts;
    }

    // -- Public getters --

    get state(): XRSessionState {
        return this._state;
    }

    get isSupported(): boolean {
        return this._supported;
    }

    get isActive(): boolean {
        return this._state === XRSessionState.ACTIVE;
    }

    // -- Public API --

    /**
     * Enable the built-in AR button UI. When enabled, an "Enter AR" overlay
     * button is shown if the device supports immersive-ar. By default the
     * UI is off — consumers opt in via the `xr` attribute on `<trace-viewer>`.
     */
    enableUI(): void {
        this._uiEnabled = true;
        // If support was already determined, show the button immediately
        if (this._supported) {
            this._createUI();
        }
    }

    /**
     * Check if immersive-ar is supported on this device/browser.
     */
    async checkSupport(): Promise<boolean> {
        if (typeof navigator === "undefined" || !navigator.xr) {
            Logger.log("[XRManager] navigator.xr not available");
            this._supported = false;
            this._state = XRSessionState.NOT_SUPPORTED;
            return false;
        }

        try {
            const supported = await navigator.xr.isSessionSupported("immersive-ar");
            Logger.log(`[XRManager] immersive-ar supported: ${supported}`);
            this._supported = supported;
            if (supported) {
                this._state = XRSessionState.AVAILABLE;
                if (this._uiEnabled) {
                    this._createUI();
                }
            } else {
                this._state = XRSessionState.NOT_SUPPORTED;
                this._removeUI();
            }
            return supported;
        } catch (error) {
            Logger.warn("[XRManager] isSessionSupported check failed:", error);
            this._supported = false;
            this._state = XRSessionState.NOT_SUPPORTED;
            return false;
        }
    }

    /**
     * Configure image alignment for XR entry.
     * When set, entering XR will show a headlocked image preview for the user
     * to align with the physical image before displaying scene content.
     */
    setImageAlignment(imageUrl: string, imageWidthMeters: number): void {
        this._imageAlignmentConfig = { imageUrl, imageWidthMeters };
    }

    /** Configure one source before entry. Use exit → configure → enter to switch. Null is legacy. */
    setLocalizers(localizers: readonly ScenePlacement[] | null, selectedPlacementId?: string): void {
        if (this._state === XRSessionState.ACTIVE || this._state === XRSessionState.ENTERING)
            throw new Error('Exit XR before changing the selected marker.');
        const selection = localizers === null ? null : requireLocalizerSession(localizers, selectedPlacementId);
        this._stopAutoImageTracking();
        this._restoreAnchorHostPose();
        this._selection = selection;
    }

    private _beginTrackingSession(): { targets: ScenePlacement[]; legacy: boolean } | null {
        const tracking = this._trackingTargets();
        this._sessionLocalizers = tracking?.targets ?? [];
        this._legacyTracking = tracking?.legacy ?? false;
        this._lastScenePose = null;
        this._restoreAnchorHostPose();
        const root = this._traceScene.getSceneRoot();
        if (tracking && root) {
            const enabled = root.transform.isEnabled(false);
            this._restoreContentVisibility = () => {
                if (!root.transform.isDisposed()) root.transform.setEnabled(enabled);
            };
            this._setSceneContentVisible(false);
        }
        if (this._selection) this._restoreAnchorHost = poseAnchorHostForSession(this._traceScene, this._selection.authoredPose);
        return tracking;
    }

    private _trackingTargets(): { targets: ScenePlacement[]; legacy: boolean } | null {
        if (this._selection) return { targets: [this._selection.selected], legacy: false };
        const cfg = this._imageAlignmentConfig;
        return cfg ? { targets: [legacyImageLocalizer(cfg.imageUrl, cfg.imageWidthMeters)], legacy: true } : null;
    }

    /**
     * Enter AR mode. Must be called from a user gesture (click/tap).
     */
    async enterXR(): Promise<void> {
        if (this._state !== XRSessionState.AVAILABLE) {
            throw new Error(`Cannot enter XR: state is ${this._state}`);
        }

        try {
            const generation = ++this._generation;
            this._state = XRSessionState.ENTERING;
            this._updateButton();
            this._emitXRStateChanged();

            if (!this._xrHelper) {
                await this._initXRHelper();
            }
            if (this._disposed || generation !== this._generation) throw new Error('XR entry was cancelled.');
            const xrHelper = this._xrHelper!; // guaranteed by _initXRHelper above

            // Enable image tracking as an optional feature before entering the
            // session. _extendXRSessionInitObject (called inside enterXRAsync)
            // collects enabled features and adds "image-tracking" to
            // optionalFeatures + passes trackedImages to the session config.
            xrHelper.featuresManager.disableFeature(WebXRFeatureName.IMAGE_TRACKING);
            this._imageTrackingFeature = null;
            const tracking = this._beginTrackingSession();
            // The overlay shows the selected marker.
            const alignmentConfig = tracking ? {
                imageUrl: tracking.targets[0].localizer!.imageUrl!,
                imageWidthMeters: tracking.targets[0].localizer!.widthM!,
            } : null;
            if (tracking) {
                try {
                    this._imageTrackingFeature = xrHelper.featuresManager.enableFeature(
                        WebXRFeatureName.IMAGE_TRACKING,
                        "latest",
                        {
                            // Index i here is IWebXRTrackedImage.id, which is
                            // how sightings map back to _sessionLocalizers[i].
                            images: tracking.targets.map((l) => ({
                                src: l.localizer!.imageUrl!,
                                estimatedRealWorldWidth: l.localizer!.widthM!,
                            })),
                        },
                        false, // not required — falls back to manual alignment
                        false,
                    );
                } catch {
                    // image-tracking not available; will fall back to manual overlay
                    this._imageTrackingFeature = null;
                }
            }

            this._makeBackgroundTransparent();
            this._detachArcRotateCamera();

            // Hide scene content immediately when entering with image alignment
            // so it's never visible before the alignment overlay appears.
            if (alignmentConfig) {
                this._setSceneContentVisible(false);
            }

            // Disable Babylon's first-frame compensation — it modifies position.y
            // and triggers _updateReferenceSpace, which would overwrite our offset.
            // We handle positioning ourselves via _positionForAR.
            xrHelper.camera.compensateOnFirstFrame = false;

            await xrHelper.enterXRAsync("immersive-ar", "unbounded");
            if (this._disposed || generation !== this._generation) {
                await xrHelper.exitXRAsync();
                throw new Error('XR entry was cancelled.');
            }
            if (alignmentConfig) {
                this._resetSpace = xrHelper.sessionManager.baseReferenceSpace;
                this._resetSpace.addEventListener('reset', this._onTrackingReset);
            }

            // Place the scene origin relative to the device on the first frame.
            // Deferred so we can read the actual device pose (heading) from the
            // XR camera, which isn't available until the first frame renders.
            if (alignmentConfig) {
                // Check if the image tracking feature actually attached.
                // Chrome behind flags may support image-tracking but not list it
                // in session.enabledFeatures, causing Babylon to skip attachment.
                // In that case, force-attach so _onXRFrame runs.
                let hasImageTracking = this._imageTrackingFeature?.attached ?? false;
                if (!hasImageTracking && this._imageTrackingFeature) {
                    // The base class attach(force) accepts a boolean, but the
                    // WebXRImageTracking override omits it from its type declaration.
                    // Cast to the base class to call with force=true.
                    hasImageTracking = (this._imageTrackingFeature as WebXRAbstractFeature).attach(true);
                    if (hasImageTracking) {
                        Logger.log("[XRManager] Image tracking force-attached (not in session.enabledFeatures)");
                    }
                }

                // Always show the manual alignment overlay. When image tracking
                // is available, also subscribe in parallel — whichever fires
                // first (auto-detect or user tap) wins and cancels the other.
                this._startImageAlignment(alignmentConfig, hasImageTracking);

                if (hasImageTracking && this._imageTrackingFeature) {
                    this._startAutoImageTracking(this._imageTrackingFeature);
                }
            } else {
                // Standard mode: position 3m in front of user
                const arcCamera = this._traceScene.getCamera();
                if (arcCamera) {
                    this._positionForAROnFirstFrame(arcCamera);
                }
            }
        } catch (error) {
            // Rollback on failure
            Logger.error("[XRManager] Failed to enter AR:", error);
            this._xrHelper?.featuresManager.disableFeature(WebXRFeatureName.IMAGE_TRACKING);
            this._stopAutoImageTracking();
            this._disposeImageAlignment();
            this._restoreAnchorHostPose();
            this._restoreBackground();
            this._reattachArcRotateCamera();
            this._state = this._disposed ? XRSessionState.NOT_SUPPORTED : XRSessionState.AVAILABLE;
            this._updateButton();
            this._emitXRStateChanged();
            throw error;
        }
    }

    /**
     * Exit AR mode and return to normal 3D viewing.
     */
    async exitXR(): Promise<void> {
        if (this._state !== XRSessionState.ACTIVE) return;

        // Abort alignment/tracking if in progress
        this._stopAutoImageTracking();
        this._disposeImageAlignment();

        this._state = XRSessionState.EXITING;
        this._updateButton();
        this._emitXRStateChanged();

        await this._xrHelper?.exitXRAsync();
        // Cleanup runs via onStateChangedObservable -> _onStateChanged(NOT_IN_XR)
    }

    // -- Disposal --

    dispose(): void {
        this._disposed = true;
        if (this._state === XRSessionState.ACTIVE && this._xrHelper) {
            this._xrHelper.exitXRAsync().catch(() => {});
        }

        this._teardownXRDrag();
        this._disposeImageAlignment();
        this._stopAutoImageTracking();
        this._restoreAnchorHostPose();
        this._imageTrackingFeature = null;
        this._xrInput?.dispose();
        this._xrInput = null;
        this._xrHelper?.dispose();
        this._xrHelper = null;
        this._removeUI();
        this._savedClearColor = null;
        this._state = XRSessionState.NOT_SUPPORTED;
    }

    // -- Internal --

    private async _initXRHelper(): Promise<void> {
        const helper = await WebXRExperienceHelper.CreateAsync(this._scene);
        if (this._disposed) { helper.dispose(); throw new Error("XR manager was disposed during initialization."); }
        this._xrHelper = helper;

        this._xrHelper.onStateChangedObservable.add((state) => {
            this._onStateChanged(state);
        });

        // Enable DOM Overlay so the Exit AR button is visible during the XR session
        if (this._overlayContainer) {
            this._xrHelper.featuresManager.enableFeature(
                WebXRDomOverlay,
                "latest",
                { element: this._overlayContainer },
                undefined,
                false // not required — gracefully degrades if unsupported
            );
        }

        // Set up input handling so screen taps work as pointer events in AR.
        // WebXRInput detects transient-pointer input sources (screen touches),
        // and WebXRControllerPointerSelection performs raycasting for them.
        this._xrInput = new WebXRInput(
            this._xrHelper.sessionManager,
            this._xrHelper.camera,
            { doNotLoadControllerMeshes: true }
        );

        this._xrHelper.featuresManager.enableFeature(
            WebXRControllerPointerSelection,
            "latest",
            {
                xrInput: this._xrInput,
                disablePointerUpOnTouchOut: false,
                forceGazeMode: false,
                disableScenePointerVectorUpdate: false,
            },
            true, // attach immediately
            false // not required
        );
    }

    /**
     * Start automatic image tracking in parallel with the manual overlay.
     * Subscribes to the tracking observable; when a confident (non-emulated)
     * result arrives, dismisses the manual overlay and applies the tracked pose.
     *
     * Cleanup is handled by _stopAutoImageTracking, called when alignment
     * completes (either auto or manual) or when the session ends.
     */
    private _startAutoImageTracking(feature: WebXRImageTracking): void {
        this._imageTrackingFeature = feature;
        if (!this._resetSpace && this._xrHelper) {
            this._resetSpace = this._xrHelper.sessionManager.baseReferenceSpace;
            this._resetSpace.addEventListener('reset', this._onTrackingReset);
        }
        this._lastScenePose = null;
        this._stabilizer = this._legacyTracking ? null : new LocalizerStabilizer(this._sessionLocalizers[0]);
        this._subscribeTracking();
    }

    private _subscribeTracking(): void {
        const feature = this._imageTrackingFeature;
        if (!feature) return;
        const generation = this._generation;
        this._imageTrackingObserver = feature.onTrackedImageUpdatedObservable.add((trackedImage: IWebXRTrackedImage) => {
            if (generation !== this._generation || this._lastScenePose || trackedImage.emulated || trackedImage.id !== 0) return;
            const worldFromL = this._nativeTrackedImagePose(trackedImage);
            if (!worldFromL) return;
            const accepted = this._legacyTracking ? worldFromL : this._stabilizer?.observe(worldFromL, nowSeconds());
            if (accepted) {
                this._applyTrackedScenePose(accepted);
                this._unsubscribeTracking();
            }
        });
    }

    private _unsubscribeTracking(): void {
        if (this._imageTrackingObserver) this._imageTrackingFeature?.onTrackedImageUpdatedObservable.remove(this._imageTrackingObserver);
        this._imageTrackingObserver = null;
    }

    private _stopAutoImageTracking(): void {
        this._generation++;
        this._unsubscribeTracking();
        this._resetSpace?.removeEventListener('reset', this._onTrackingReset);
        this._resetSpace = null;
        this._stabilizer = null;
    }

    private _restoreAnchorHostPose(): void {
        this._restoreAnchorHost?.();
        this._restoreAnchorHost = null;
        this._restoreContentVisibility?.();
        this._restoreContentVisibility = null;
    }

    /**
     * Apply the pose from a tracked image result to place the scene.
     *
     * Reads the native XR pose directly from the tracked result's imageSpace,
     * bypassing Babylon's LH matrix conversion. The native pose position and
     * orientation are in WebXR right-handed coordinates — exactly what
     * XRRigidTransform / getOffsetReferenceSpace expect.
     */
    private _nativeTrackedImagePose(trackedImage: IWebXRTrackedImage): RigidPose | null {
        if (!this._xrHelper) return null;
        const sessionManager = this._xrHelper.sessionManager;
        const xrFrame = sessionManager.currentFrame;
        const nativeResult = trackedImage.xrTrackingResult;

        if (!xrFrame || !nativeResult) {
            Logger.warn("[XRManager] No XR frame or native tracking result — skipping");
            return null;
        }

        // Get the native RH pose relative to the base reference space
        const pose = xrFrame.getPose(nativeResult.imageSpace, sessionManager.baseReferenceSpace);
        if (!pose) {
            Logger.warn("[XRManager] Could not get native pose for tracked image");
            return null;
        }

        const p = pose.transform.position;
        const q = pose.transform.orientation;

        if (this._legacyTracking) {
            Logger.log(
                `[XRManager] Image tracked (native) at pos=(${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)}), ` +
                `rot=(${q.x.toFixed(3)}, ${q.y.toFixed(3)}, ${q.z.toFixed(3)}, ${q.w.toFixed(3)})`
            );
        }

        // Chrome reports ARCore's augmented-image pose as the image space:
        // +X image right, +Y out of the image, +Z toward its bottom. The
        // selected localizer frame is that turned 180° about the normal:
        // q_corrected = q_image * quat(0, 1, 0, 0) → (-qz, qw, qx, -qy).
        return webxrImagePoseToLocalizer(p, q);
    }

    /**
     * Place the scene at `T_world←scene` (world = the base reference space)
     * by offsetting the reference space, as the single-marker path always
     * has. The first call also dismisses the manual overlay and shows the
     * content.
     */
    private _applyTrackedScenePose(pose: RigidPose): void {
        if (!this._xrHelper) return;
        const sessionManager = this._xrHelper.sessionManager;
        const first = this._lastScenePose === null;
        this._lastScenePose = pose;

        // Dismiss manual overlay if still showing
        this._disposeImageAlignment();

        const transform = new XRRigidTransform(
            { x: pose.t[0], y: pose.t[1], z: pose.t[2], w: 1 },
            { x: pose.q[0], y: pose.q[1], z: pose.q[2], w: pose.q[3] },
        );
        sessionManager.referenceSpace = sessionManager.baseReferenceSpace.getOffsetReferenceSpace(transform);

        // Shadow plane is always gravity-aligned; only position follows
        // the anchor. The rotation argument is intentionally identity.
        const position = new Vector3(pose.t[0], pose.t[1], -pose.t[2]);
        this._traceScene.setShadowPosition(position);

        if (first) this._setSceneContentVisible(true);
    }

    /**
     * Start the image alignment flow on the first XR frame.
     * Hides scene content, shows the headlocked image preview, and waits for
     * the user to confirm alignment before placing the scene.
     *
     * @param config - image URL and physical width
     * @param autoTracking - if true, instruction text indicates scanning is active
     */
    private _startImageAlignment(config: { imageUrl: string; imageWidthMeters: number }, autoTracking: boolean): void {
        if (!this._xrHelper) return;
        const xrHelper = this._xrHelper;
        Logger.log("[XRManager] Scheduling image alignment for first XR frame");

        const generation = this._generation;
        const observer = this._scene.onBeforeRenderObservable.addOnce(() => {
            if (generation !== this._generation || this._lastScenePose) return;
            const xrCamera = xrHelper.camera;
            Logger.log(`[XRManager] First XR frame — xrCamera pos: (${xrCamera.position.x.toFixed(2)}, ${xrCamera.position.y.toFixed(2)}, ${xrCamera.position.z.toFixed(2)})`);

            // Hide scene content during alignment so only the overlay is visible
            this._setSceneContentVisible(false);

            this._imageAlignment = new ImageAlignmentOverlay({
                imageUrl: config.imageUrl,
                imageWidthMeters: config.imageWidthMeters,
                scene: this._scene,
                xrCamera,
                autoTracking,
            });

            this._imageAlignment.onConfirm((result: AlignmentResult) => {
                if (generation !== this._generation) return;
                this._confirmImageAlignmentPose(result);
                this._imageAlignment?.dispose();
                this._imageAlignment = null;

                // Re-show scene content now that alignment is complete
                this._setSceneContentVisible(true);

                // Log scene root position after alignment for debugging
                const sceneRoot = this._traceScene.getSceneRoot();
                if (sceneRoot) {
                    sceneRoot.transform.computeWorldMatrix(true);
                    const worldPos = sceneRoot.transform.getAbsolutePosition();
                    Logger.log(`[XRManager] Image alignment complete. Scene root world pos: (${worldPos.x.toFixed(3)}, ${worldPos.y.toFixed(3)}, ${worldPos.z.toFixed(3)})`);
                } else {
                    Logger.log("[XRManager] Image alignment complete (no scene root)");
                }
            });

            this._imageAlignment.show();
        });

        // Safety: clean up if session ends during alignment.
        // Use a persistent observer so intermediate state changes don't consume it.
        const stateObserver = xrHelper.onStateChangedObservable.add((state) => {
            if (state === WebXRState.NOT_IN_XR || state === WebXRState.EXITING_XR) {
                xrHelper.onStateChangedObservable.remove(stateObserver);
                this._scene.onBeforeRenderObservable.remove(observer);
                this._disposeImageAlignment();
                this._stopAutoImageTracking();
            }
        });
    }

    /** Manual confirmation is final until reset or explicit restart. */
    private _confirmImageAlignmentPose(result: AlignmentResult): void {
        if (this._lastScenePose) return;
        this._unsubscribeTracking();
        this._lastScenePose = this._applyImageAlignmentPose(result);
    }

    /**
     * Apply the confirmed image alignment pose to the XR reference space.
     * The scene origin is placed at the image's world position with the
     * full orientation from the overlay (preserves pitch for wall vs
     * table targets).
     *
     * The OpenXR anchor frame the editor uses end-to-end (set up by the
     * `.zip` loader at import time) has +X = image-right,
     * +Y = image-top, +Z = image-normal. The overlay's
     * `FromLookDirectionLH(imageNormal, camUp)` returns a quaternion in
     * an "OpenGL-camera-style" frame where local -Z points along
     * imageNormal. Composing with `Ry(π)` flips local X and Z so the
     * result expresses the OpenXR anchor frame in world space.
     */
    private _applyImageAlignmentPose(result: AlignmentResult): RigidPose | null {
        if (!this._xrHelper) return null;
        const sessionManager = this._xrHelper.sessionManager;
        const pos = result.position;
        const q = result.rotationQuaternion;

        // Rotate the overlay's camera-style frame into the OpenXR anchor
        // frame the rest of the runtime consumes.
        const overlayToOpenXR = Quaternion.RotationAxis(Vector3.Up(), Math.PI);
        const finalQ = q.multiply(overlayToOpenXR);

        Logger.log(
            `[XRManager] Alignment pose: pos=(${pos.x.toFixed(3)}, ${pos.y.toFixed(3)}, ${pos.z.toFixed(3)}), ` +
            `alignQ=(${q.x.toFixed(3)}, ${q.y.toFixed(3)}, ${q.z.toFixed(3)}, ${q.w.toFixed(3)}), ` +
            `finalQ=(${finalQ.x.toFixed(3)}, ${finalQ.y.toFixed(3)}, ${finalQ.z.toFixed(3)}, ${finalQ.w.toFixed(3)})`
        );

        // With localizers, the overlay aligned the selected marker, so the
        // scene goes where that placement puts it: T_world←L · P⁻¹, snapped
        // upright about the marker when it is near its authored attitude
        // (single-source snap policy).
        let offset: RigidPose = { t: [pos.x, pos.y, pos.z], q: [finalQ.x, finalQ.y, finalQ.z, finalQ.w] };
        const primary = this._sessionLocalizers[0];
        if (!this._legacyTracking && primary) {
            const markerInScene: [number, number, number] = [primary.t[0], primary.t[1], primary.t[2]];
            offset = poseAutoSnapped(sceneFromObservation(offset, {
                t: markerInScene,
                q: [primary.q[0], primary.q[1], primary.q[2], primary.q[3]],
            }), markerInScene);
        }

        const transform = new XRRigidTransform(
            { x: offset.t[0], y: offset.t[1], z: offset.t[2], w: 1 },
            { x: offset.q[0], y: offset.q[1], z: offset.q[2], w: offset.q[3] },
        );
        sessionManager.referenceSpace = sessionManager.baseReferenceSpace.getOffsetReferenceSpace(transform);

        // Sync shadow ground plane to the alignment position, but keep its
        // orientation world-axis-aligned (identity rotation). The shadow
        // plane is gravity-aligned regardless of marker orientation — for a
        // wall marker, shadows fall on the room floor, not on the wall.
        this._traceScene.setShadowPosition(new Vector3(offset.t[0], offset.t[1], offset.t[2]));
        return offset;
    }

    /**
     * Dispose the image alignment overlay and re-enable scene content.
     */
    private _disposeImageAlignment(): void {
        if (this._imageAlignment) {
            this._imageAlignment.dispose();
            this._imageAlignment = null;

            // Safety net: re-enable scene content if session ends during alignment
            this._setSceneContentVisible(true);
        }
    }

    /**
     * Show or hide scene content (the scene root and all its children).
     * Used to hide content during image alignment so only the overlay is visible.
     * Alignment overlay meshes are NOT children of the scene root, so they stay visible.
     */
    private _setSceneContentVisible(visible: boolean): void {
        const sceneRoot = this._traceScene.getSceneRoot();
        if (sceneRoot) {
            sceneRoot.transform.setEnabled(visible);
            Logger.log(`[XRManager] Scene content ${visible ? "shown" : "hidden"}`);
        }
    }

    /**
     * Wait for the first XR frame, read the device's actual pose, then place
     * the scene origin 3m in front of the user's XZ view direction at ground
     * level (head.y − 1.6m).
     *
     * We must defer to the first frame because the device's real heading isn't
     * available until a pose is reported — baseReferenceSpace alone doesn't
     * tell us which way the user is facing.
     */
    private _positionForAROnFirstFrame(arcCamera: ArcRotateCamera): void {
        if (!this._xrHelper) return;
        const xrHelper = this._xrHelper;
        const observer = this._scene.onBeforeRenderObservable.addOnce(() => {
            const sessionManager = xrHelper.sessionManager;
            const xrCamera = xrHelper.camera;

            const pos = xrCamera.position;
            const forward = xrCamera.getForwardRay().direction;

            // Project forward onto XZ plane and normalize
            const fwdX = forward.x;
            const fwdZ = forward.z;
            const len = Math.sqrt(fwdX * fwdX + fwdZ * fwdZ);

            const forwardDistance = 3;
            const headToGround = 1.6;

            let dirX: number, dirZ: number;
            if (len > 0.001) {
                dirX = fwdX / len;
                dirZ = fwdZ / len;
            } else {
                dirX = 0;
                dirZ = -1;
            }

            // Target position: 3m forward in XZ from head, at ground level
            const tx = pos.x + dirX * forwardDistance;
            const ty = pos.y - headToGround;
            const tz = pos.z + dirZ * forwardDistance;

            // Device heading angle: angle of forward direction from -Z axis
            const deviceYaw = Math.atan2(fwdX, fwdZ);

            // Rotate the scene so the editor camera's viewed face points
            // toward the device. Combine editor alpha with device heading.
            // Rotate scene to face the user. editorYaw orients the scene so the
            // face the editor camera saw aligns with -Z. deviceYaw accounts for
            // the device's actual heading. +PI flips the scene to face back
            // toward the user rather than away.
            const editorYaw = arcCamera.alpha - Math.PI / 2;
            const yaw = editorYaw + deviceYaw + Math.PI;
            const halfYaw = yaw / 2;

            Logger.log(`[XRManager] AR: head=(${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}), fwd=(${dirX.toFixed(2)}, ${dirZ.toFixed(2)})`);
            Logger.log(`[XRManager] AR: target=(${tx.toFixed(2)}, ${ty.toFixed(2)}, ${tz.toFixed(2)}), yaw=${(yaw * 180 / Math.PI).toFixed(1)}deg`);

            // Apply as two steps: translate first, then rotate around the new origin.
            // getOffsetReferenceSpace(T) means: new origin = T applied in old space.
            // Step 1: Move origin to target position
            const translation = new XRRigidTransform(
                { x: tx, y: ty, z: tz, w: 1 },
            );
            // Step 2: Rotate around the new origin
            const rotation = new XRRigidTransform(
                { x: 0, y: 0, z: 0, w: 1 },
                { x: 0, y: Math.sin(halfYaw), z: 0, w: Math.cos(halfYaw) },
            );
            const translated = sessionManager.baseReferenceSpace.getOffsetReferenceSpace(translation);
            sessionManager.referenceSpace = translated.getOffsetReferenceSpace(rotation);

            // Shadow plane is always gravity-aligned; only position follows
            // the recentered anchor.
            this._traceScene.setShadowPosition(new Vector3(tx, ty, tz));
        });

        // Safety: clean up if session ends before the first frame fires.
        // Use a persistent observer so intermediate state changes don't consume it.
        const stateObserver = xrHelper.onStateChangedObservable.add((state) => {
            if (state === WebXRState.NOT_IN_XR || state === WebXRState.EXITING_XR) {
                xrHelper.onStateChangedObservable.remove(stateObserver);
                this._scene.onBeforeRenderObservable.remove(observer);
            }
        });
    }

    // -- XR drag interaction --
    // When XR is active, any selected object gets a temporary SixDofDragBehavior
    // so users can grab and move it with hands. Rotation is constrained to
    // Y-axis only (no tilt from hand pitch/roll).

    private _setupXRDrag(): void {
        const events = this._traceScene.getEventSystem();
        if (!events) return;

        // Hide collider debug visualizations — they don't render correctly in stereo
        this._traceScene.setCollidersDebugVisible(false);

        this._xrDragSelectionHandler = (e: Event) => {
            const detail = (e as CustomEvent<{ object?: SceneObject | null }>).detail;
            void this._onXRSelectionChanged(detail?.object ?? null);
        };
        events.addEventListener("trace-viewer:selection-changed", this._xrDragSelectionHandler);
    }

    private _teardownXRDrag(): void {
        // Remove selection listener
        if (this._xrDragSelectionHandler) {
            const events = this._traceScene.getEventSystem();
            events?.removeEventListener("trace-viewer:selection-changed", this._xrDragSelectionHandler);
            this._xrDragSelectionHandler = null;
        }

        // Detach any active 6DOF drag
        this._detachXRDragFromTarget();
    }

    private async _onXRSelectionChanged(object: SceneObject | null): Promise<void> {
        // Always detach from previous target
        this._detachXRDragFromTarget();

        if (!object || this._state !== XRSessionState.ACTIVE) return;

        // Capture a selection epoch so rapid selection changes during the
        // async import don't attach 6DoF to a stale target.
        const epoch = ++this._xrDragSelectionEpoch;

        const { SixDofDragBehavior } = await import("@babylonjs/core/Behaviors/Meshes/sixDofDragBehavior");

        if (this._state !== XRSessionState.ACTIVE || epoch !== this._xrDragSelectionEpoch) return;

        // SixDofDragBehavior's NEAR_DRAG path (hand grab) always applies
        // rotation regardless of `rotateDraggedObject` — it copies the hand
        // delta straight into `_targetOrientation`. The full hand orientation
        // flows in, so hand pitch/roll would tilt the object. We override
        // `_targetDrag` to swing-twist project the delta to the Y axis first;
        // see `projectQuaternionToYaw`.
        //
        // `rotateDraggedObject = false` only suppresses rotation in the
        // controller-DRAG path (one-pointer pinch with a motion controller),
        // which we don't use here — combined with `rotateWithMotionController
        // = false`, the controller path is position-only.
        const Ctor = getYawOnlySixDofCtor(SixDofDragBehavior);
        const sixDof = new Ctor();
        sixDof.rotateWithMotionController = false;
        sixDof.rotateDraggedObject = false;
        sixDof.detachCameraControls = false; // don't interfere with XR pointer
        sixDof.dragDeltaRatio = 1;
        sixDof.allowMultiPointer = false;

        // SixDofDragBehavior is typed for Mesh but only calls TransformNode
        // members on the attached node. Cast is load-bearing.
        sixDof.attach(object.transform as unknown as Mesh);

        // SixDofDragBehavior.attach() sets `isNearGrabbable = true` on the
        // owner AND all descendant meshes. That makes interactive overlays
        // (e.g. Video timeline controls) unreachable because near-grab
        // outranks pointer-ray picks. Opt them back out by suffix.
        const children = object.transform.getChildMeshes?.() ?? [];
        for (const m of children) {
            if (m.name.endsWith(CONTROLS_MESH_SUFFIX)) {
                m.isNearGrabbable = false;
            }
        }

        // Tell ClickToSelect a drag is underway so the POINTERUP at
        // release isn't interpreted as a click. Without this, hand grabs
        // trigger e.g. Video tap-to-play on release because the XR
        // pointer's screen-space delta is near zero. Store the observer
        // handle so _detachXRDragFromTarget can remove it — today sixDof is
        // replaced per selection (so the closure is unreachable after
        // detach), but storing the handle is cheap and robust if
        // _attachSixDofDrag ever caches the instance.
        this._xrDragStartObserver = sixDof.onDragStartObservable.add(() => {
            this._clickToSelect?.notifyDragOccurred();
        });

        this._xrDragSixDof = sixDof;
        this._xrDragTarget = object;
    }

    private _detachXRDragFromTarget(): void {
        if (this._xrDragSixDof) {
            if (this._xrDragStartObserver) {
                this._xrDragSixDof.onDragStartObservable.remove(this._xrDragStartObserver);
                this._xrDragStartObserver = null;
            }
            this._xrDragSixDof.detach();
            this._xrDragSixDof = null;
        }
        this._xrDragTarget = null;
    }

    private _onStateChanged(state: WebXRState): void {
        // dispose() may have fired exitXRAsync() which later re-enters here;
        // skip so we don't touch already-freed resources.
        if (this._disposed) return;
        switch (state) {
            case WebXRState.ENTERING_XR:
                this._state = XRSessionState.ENTERING;
                // Before the first XR frame: occluders go depth-only for the session.
                this._traceScene.setXRSessionActive(true);
                break;
            case WebXRState.IN_XR:
                this._state = XRSessionState.ACTIVE;
                this._setupXRDrag();
                break;
            case WebXRState.EXITING_XR:
                this._state = XRSessionState.EXITING;
                break;
            case WebXRState.NOT_IN_XR:
                // Handles both explicit exit and unexpected session end
                this._traceScene.setXRSessionActive(false);
                this._teardownXRDrag();
                this._stopAutoImageTracking();
                this._imageTrackingFeature = null;
                this._restoreAnchorHostPose();
                this._restoreBackground();
                this._reattachArcRotateCamera();
                this._state = XRSessionState.AVAILABLE;
                break;
        }
        this._updateButton();
        this._emitXRStateChanged();
    }

    private _makeBackgroundTransparent(): void {
        this._savedClearColor = this._scene.clearColor.clone();
        this._scene.clearColor = new Color4(0, 0, 0, 0);
        Logger.log("[XRManager] Background set to transparent for AR");
    }

    private _restoreBackground(): void {
        if (this._savedClearColor) {
            this._scene.clearColor = this._savedClearColor;
            this._savedClearColor = null;
            Logger.log("[XRManager] Background restored");
        }
    }

    private _detachArcRotateCamera(): void {
        const camera = this._traceScene.getCamera();
        if (camera) {
            camera.detachControl();
            Logger.log("[XRManager] ArcRotateCamera controls detached");
        }
    }

    private _reattachArcRotateCamera(): void {
        const camera = this._traceScene.getCamera();
        if (camera) {
            const canvas = this._scene.getEngine().getRenderingCanvas();
            if (canvas) {
                camera.attachControl(canvas, true);
            }
            // Ensure the orbit camera is active again
            this._scene.activeCamera = camera;
            Logger.log("[XRManager] ArcRotateCamera controls reattached");
        }
    }

    // -- UI --

    /**
     * Create the overlay container and AR button.
     * The overlay container is registered with WebXR DOM Overlay so the button
     * remains visible and interactive during the immersive-ar session.
     */
    private _createUI(): void {
        if (this._overlayContainer) return;

        // Overlay container — spans the full viewer area.
        // Registered as the DOM overlay element so the browser projects it into the AR view.
        const overlay = document.createElement("div");
        overlay.setAttribute("data-trace-xr-overlay", "true");
        Object.assign(overlay.style, {
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            pointerEvents: "none", // pass through to canvas except on interactive children
            zIndex: "10",
        });

        // AR button
        const button = document.createElement("button");
        button.textContent = "View in AR";
        button.setAttribute("data-trace-xr-button", "true");

        Object.assign(button.style, {
            position: "absolute",
            bottom: "16px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "8px 20px",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            borderRadius: "20px",
            cursor: "pointer",
            pointerEvents: "auto", // button itself is interactive
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            transition: "background-color 0.2s ease",
            outline: "none",
        });

        button.addEventListener("mouseenter", () => {
            button.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        });
        button.addEventListener("mouseleave", () => {
            button.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
        });

        button.addEventListener("click", () => {
            if (this._state === XRSessionState.ACTIVE) {
                void this.exitXR();
            } else if (this._state === XRSessionState.AVAILABLE) {
                void this.enterXR();
            }
        });

        overlay.appendChild(button);
        this._parentElement.appendChild(overlay);
        this._overlayContainer = overlay;
        this._arButton = button;
    }

    private _updateButton(): void {
        if (!this._arButton) return;

        switch (this._state) {
            case XRSessionState.AVAILABLE:
                this._arButton.textContent = "View in AR";
                this._arButton.disabled = false;
                break;
            case XRSessionState.ENTERING:
                this._arButton.textContent = "Entering AR...";
                this._arButton.disabled = true;
                break;
            case XRSessionState.ACTIVE:
                this._arButton.textContent = "Exit AR";
                this._arButton.disabled = false;
                break;
            case XRSessionState.EXITING:
                this._arButton.textContent = "Exiting AR...";
                this._arButton.disabled = true;
                break;
        }
    }

    private _removeUI(): void {
        if (this._overlayContainer) {
            this._overlayContainer.remove();
            this._overlayContainer = null;
            this._arButton = null;
        }
    }

    // -- Events --

    private _emitXRStateChanged(): void {
        const evt = this._traceScene.getEventSystem();
        if (!evt) return;

        const event = new CustomEvent("trace-viewer:xr-state-changed", {
            detail: {
                state: this._state,
                isActive: this._state === XRSessionState.ACTIVE,
                isSupported: this._supported,
            },
            bubbles: true,
            composed: true,
        });
        evt.dispatchEvent(event);
        Logger.log(`[XRManager] State changed: ${this._state}`);
    }
}
